import React, { useMemo } from 'react';
import { Icons } from "../Icons";

/**
 * TabItem — Individual tab button for tab navigation.
 *
 * Compose multiple TabItem components in a row and manage the active
 * tab state in the parent. Each tab supports an optional icon and
 * three visual styles.
 *
 * Variants:
 * - 'block'      — Filled background when active (default)
 * - 'line'       — Underline indicator when active
 * - 'block-mono' — Monochrome filled background when active
 * - 'line-fill'  — Filled background on hover, underline on active
 *
 * @param {string}  label     - Tab text label
 * @param {string}  iconName  - Optional icon name shown before the label
 * @param {string}  variant   - 'block' | 'line' | 'block-mono' | 'line-fill'
 * @param {boolean} isActive  - Whether this tab is currently selected
 * @param {boolean} disabled  - Disabled state (grayed out, not clickable)
 * @param {func}    onClick   - Click handler (set the active tab in parent state)
 */
export const TabItem = ({
  label = "Tab item",
  iconName,
  variant = "block",
  disabled = false,
  isActive = false,
  onClick,
  autoFocus,
  fontClass = "body-s-semibold",
  customClass = "",
  ...props
}) => {
  const classes = useMemo(() =>
    [
      'tab-item',
      `tab-item--${variant}`,
      fontClass,
      isActive ? 'is-active' : '',
      disabled ? 'is-disabled' : '',
      customClass
    ].filter(Boolean).join(' '),
    [variant, fontClass, isActive, disabled, customClass]
  );

  return (
    <button 
      className={classes} 
      onClick={!disabled ? onClick : undefined} 
      disabled={disabled}
      autoFocus={autoFocus}
      {...props}
    >
      {iconName && <Icons name={iconName} size={16} className="tab-item-icon" />}
      <span className="tab-item-label">{label}</span>
    </button>
  );
};
